/**
 * OrgProductionDetails - LP Production View (theme from production-details.html).
 * Data from live org APIs only - no sample people.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LpWorkbenchShell from '../components/org/LpWorkbenchShell';
import { orgAuthApi } from '../services/api';
import { getLoginPath, getOrgSlatePath } from '../utils/postLoginNavigation';
import { countryNameFromIso2, formatLongDate, formatParticipantNationality, formatScheduleDate, getLpName } from '../utils/orgLpHelpers';
import {
  buildReferenceImmigrationSummary,
  buildReferenceLocationSummary,
  buildRosterRoleOptions,
  filterImmigrationPeople,
  filterRosterPeople,
  immigrationListStatus,
  invitationEmailTemplate,
  isImmigrationAtRisk,
  needsImmigration,
  productionRegisterDisplayUrl,
} from '../utils/lpProductionHelpers';
import '../styles/LpWorkbench.css';

const TASK_ROLES_ABOVE = ['Director', 'Producer', 'Lead actor', 'Supporting actor', 'Writer'];
const TASK_ROLES_BELOW = [
  'Director of photography', 'Gaffer', 'Key grip', 'Grip', 'Sound mixer',
  'Costume designer', 'Production designer', 'Makeup artist', 'Stunt coordinator',
  'Camera operator', 'Editor', 'Location manager',
];

function projectFileStem(name) {
  const stem = String(name || 'Production').trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return stem || 'Production';
}

/** Normalize API dates to yyyy-mm-dd for <input type="date">. */
function toDateInputValue(isoOrDate) {
  if (!isoOrDate) return '';
  const match = String(isoOrDate).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

/** Register default: 14 days before first shoot / schedule start. */
function registerDueFromScheduleStart(scheduleStart) {
  const raw = toDateInputValue(scheduleStart);
  if (!raw) return '';
  const d = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() - 14);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function taskPresetForTitle(title, projectName, { scheduleStart } = {}) {
  const stem = projectFileStem(projectName);
  const presets = {
    'Register for the production': {
      type: 'form',
      doc: null,
      due: registerDueFromScheduleStart(scheduleStart),
      fixed: true,
    },
    'Sign the production NDA': { type: 'sign', doc: `${stem}_NDA.pdf`, due: '2026-05-20', fixed: true },
    'Sign the NDA': { type: 'sign', doc: `${stem}_NDA.pdf`, due: '2026-05-20', fixed: true },
    'Upload travel itinerary': { type: 'upload', doc: null, due: '2026-05-15', fixed: true },
    'Travel itinerary': { type: 'upload', doc: null, due: '2026-05-15', fixed: true },
    'Acknowledge on-set safety policy': { type: 'ack', doc: 'Safety_Guidelines.pdf', due: '2026-05-28', fixed: true },
    'Safety acknowledgment': { type: 'ack', doc: 'Safety_Guidelines.pdf', due: '2026-05-28', fixed: true },
    'Emergency contact': { type: 'form', doc: null, due: '', fixed: true },
    'Code of conduct': { type: 'ack', doc: null, due: '', fixed: true },
  };
  return presets[title] || { type: 'sign', doc: null, due: '', fixed: false };
}

const TASK_ACTIONS = [
  {
    id: 'sign',
    icon: '✎',
    name: 'Sign a document',
    desc: 'People add a signature to a document you provide.',
    docLabel: 'Document to sign',
  },
  {
    id: 'ack',
    icon: '✓',
    name: 'Acknowledge a document',
    desc: 'People confirm they have read a document you provide.',
    docLabel: 'Document to acknowledge',
  },
  {
    id: 'upload',
    icon: '↑',
    name: 'Upload a document',
    desc: 'People send you a file, such as a passport or itinerary.',
    docLabel: 'Document requested',
  },
  {
    id: 'form',
    icon: '▤',
    name: 'Complete a form',
    desc: 'People fill in fields you define, such as an emergency contact.',
    docLabel: '',
  },
];

const TASK_TONES = [
  { id: 'friendly', name: 'Friendly', desc: 'Warm and personable, encouraging.' },
  { id: 'professional', name: 'Professional', desc: 'Polite and neutral.' },
  { id: 'firm', name: 'Firm', desc: 'Direct and businesslike.' },
  { id: 'urgent', name: 'Urgent', desc: 'Short and pointed, real time pressure.' },
];

const STEP_LABELS = {
  action: 'Setup',
  doc: 'Setup',
  deadline: 'Deadline',
  reminders: 'Message',
  people: 'People',
};

/**
 * "Add widget" picker entries. `toolId` marks the ones the backend can persist
 * (see enableProductionRequest); the rest configure a request locally.
 */
const WIDGET_OPTIONS = [
  {
    id: 'upload',
    name: 'Document upload',
    desc: 'Ask participants to upload a document.',
    type: 'upload',
    toolId: 'itinerary',
    custom: false,
  },
  {
    id: 'ack',
    name: 'Acknowledgement',
    desc: 'Ask participants to confirm that they have read production information.',
    type: 'sign',
    toolId: 'send_document',
    custom: false,
  },
  {
    id: 'custom',
    name: 'Custom task',
    desc: 'Define another participant request.',
    type: 'upload',
    toolId: 'custom',
    custom: true,
  },
];

function InviteTemplateModal({ project, producerName, onClose }) {
  const tpl = invitationEmailTemplate({
    projectName: project?.name,
    signupUrl: productionRegisterDisplayUrl(
      project?.customer_signup_ref,
      project?.customer_signup_url,
    ),
    producerName,
    productionCode: project?.customer_signup_ref,
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  async function copyMail() {
    const text = `Subject: ${tpl.subject}\n\n${tpl.bodyText}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return createPortal(
    <div className="lp-invite-mail-wrap" role="dialog" aria-modal="true" aria-label="Invitation email" onClick={handleBackdrop}>
      <div className="lp-invite-mail-panel" onClick={(e) => e.stopPropagation()}>
        <div className="lp-invite-mail-toolbar">
          <div>
            <h2>Invitation email</h2>
            <p>Preview of the invitation Global Mobility sends from noreply@globalmobility.law.</p>
          </div>
          <button type="button" className="lp-invite-mail-x" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="lp-invite-mail-subject">
          <span>Subject</span>
          <strong>{tpl.subject}</strong>
        </div>
        <div
          className="lp-invite-mail-preview"
          dangerouslySetInnerHTML={{ __html: tpl.bodyHtml }}
        />
        <div className="lp-invite-mail-foot">
          <button type="button" className="lp-bn-btn" onClick={copyMail}>
            {copied ? 'Copied' : 'Copy email'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function NotRegisteredModal({ productionId, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await orgAuthApi.getNotRegisteredParticipants(productionId);
        if (!cancelled) setList(res.data?.participants || []);
      } catch {
        if (!cancelled) setList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productionId]);

  async function copyList() {
    const text = list.map((p) => `${p.name || '-'}\t${p.email || ''}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="lp-modal-wrap" role="dialog" aria-modal="true">
      <div className="lp-modal lp-modal-nr">
        <div className="lp-modal-h">
          Not registered
          <button type="button" className="x" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="lp-modal-sub">These people have not registered yet. Copy the list to follow up.</div>
        <div className="lp-nrlist">
          {loading ? (
            <div className="lp-empty">Loading…</div>
          ) : list.length === 0 ? (
            <div className="lp-empty">Everyone has registered.</div>
          ) : (
            list.map((p) => (
              <div key={p.id} className="lp-nrrow">
                <span className="nm">{p.name || '-'}</span>
                <span className="em">{p.email}</span>
                <span className="rm">{p.job_title || ''}</span>
              </div>
            ))
          )}
        </div>
        <button type="button" className="lp-bn-btn" onClick={copyList} disabled={!list.length || loading}>
          {copied ? 'Copied' : 'Copy list'}
        </button>
      </div>
    </div>
  );
}

function AddWidgetModal({ options, onPick, onClose }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return createPortal(
    <div className="lp-wz-wrap" role="dialog" aria-modal="true" aria-label="Add task" onClick={handleBackdrop}>
      <div className="lp-wz lp-wz-picker" onClick={(e) => e.stopPropagation()}>
        <div className="lp-wz-h lp-wz-h-plain">
          <div className="lp-wz-headcopy">
            <h2 className="lp-wz-title">Add task</h2>
            <p className="lp-wz-eyebrow">Task type</p>
          </div>
          <button type="button" className="lp-wz-x" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="lp-wz-grid">
          {options.map((opt) => (
            <button type="button" className="lp-wz-pick" key={opt.id} onClick={() => onPick(opt)}>
              <span className="lp-wz-pickmain">
                <span className="lp-wz-pickname">{opt.name}</span>
                <span className="lp-wz-pickdesc">{opt.desc}</span>
              </span>
              <span className="lp-wz-pickgo" aria-hidden="true">&gt;</span>
            </button>
          ))}
        </div>
        <div className="lp-wz-foot lp-wz-foot-picker">
          <button type="button" className="lp-wz-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function TaskSettingsModal({
  setup,
  project,
  people,
  shootCountryName,
  countryOptions,
  onClose,
  onBack,
  onCreated,
}) {
  const title = setup?.title || '';
  const toolId = setup?.toolId || null;
  const forcedType = setup?.type || null;
  const isCustom = Boolean(setup?.custom) || (!title && !forcedType);

  const [stepIdx, setStepIdx] = useState(0);
  const [name, setName] = useState(title || '');
  const [taskType, setTaskType] = useState(forcedType || 'sign');
  const [docAttached, setDocAttached] = useState(Boolean(setup?.doc));
  const [docName, setDocName] = useState(setup?.doc || '');
  const [dueMode, setDueMode] = useState('date');
  const [due, setDue] = useState('');
  const [tone, setTone] = useState('professional');
  const [sendWhen, setSendWhen] = useState('now');
  const [needsImm, setNeedsImm] = useState(false);
  const [role, setRole] = useState('');
  const [country, setCountry] = useState('');
  const [search, setSearch] = useState('');
  const fileRef = useRef(null);

  const flow = useMemo(() => {
    const steps = [];
    // A Setup step only exists when there is something to configure up front:
    // a custom request needs a name and an action, and a document the producer
    // supplies needs uploading. Participant-provided files need neither.
    if (isCustom) steps.push('action');
    else if (taskType === 'sign' || taskType === 'ack') steps.push('doc');
    steps.push('deadline', 'reminders', 'people');
    return steps;
  }, [isCustom, taskType]);

  useEffect(() => {
    const p = taskPresetForTitle(title, project?.name, {
      scheduleStart: setup?.scheduleStart,
    });
    const nextType = forcedType || (isCustom ? 'upload' : p.type) || 'sign';
    const initialDoc = setup?.doc || (!setup?.fixed && !isCustom ? p.doc : null) || null;
    const seededDue = toDateInputValue(setup?.due)
      || p.due
      || (title === 'Register for the production'
        ? registerDueFromScheduleStart(setup?.scheduleStart)
        : '');
    setStepIdx(0);
    setName(title || '');
    setTaskType(nextType);
    setDocAttached(Boolean(initialDoc));
    setDocName(initialDoc || '');
    setDueMode('date');
    setDue(seededDue);
    setTone('professional');
    setSendWhen('now');
    setNeedsImm(false);
    setRole('');
    setCountry('');
    setSearch('');
  }, [title, project?.name, forcedType, setup?.doc, setup?.due, setup?.scheduleStart, setup?.fixed, isCustom]);

  useEffect(() => {
    if (stepIdx >= flow.length) setStepIdx(Math.max(0, flow.length - 1));
  }, [flow, stepIdx]);

  const roleOptions = useMemo(() => {
    const fromRoster = buildRosterRoleOptions(people);
    return {
      above: fromRoster.above.length ? fromRoster.above : TASK_ROLES_ABOVE,
      below: fromRoster.below.length ? fromRoster.below : TASK_ROLES_BELOW,
    };
  }, [people]);

  const countries = useMemo(() => {
    if (countryOptions?.length) return countryOptions;
    if (shootCountryName) return [shootCountryName];
    return ['Italy', 'United Kingdom'];
  }, [countryOptions, shootCountryName]);

  const assigned = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (people || []).filter((p) => {
      if (needsImm && !needsImmigration(p)) return false;
      if (role === '__above' && p.position_tier !== 'above_the_line') return false;
      if (role === '__below' && p.position_tier === 'above_the_line') return false;
      if (role && role !== '__above' && role !== '__below' && (p.job_title || '') !== role) return false;
      if (country && shootCountryName && country !== shootCountryName) return false;
      if (q && !String(p.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [people, needsImm, role, country, search, shootCountryName]);

  const assignLabel = search.trim()
    ? `${assigned.length} of ${people?.length || 0} shown`
    : `${assigned.length} people`;

  const currentStep = flow[stepIdx] || 'action';
  const isLast = stepIdx === flow.length - 1;
  const actionMeta = TASK_ACTIONS.find((a) => a.id === taskType) || TASK_ACTIONS[0];
  const headerTitle = setup?.heading || name.trim() || title || 'New request';
  const saveLabel = title && !setup?.fixed && !isCustom ? 'Save changes' : 'Create request';

  function attachDoc(file) {
    const stem = projectFileStem(project?.name);
    const fallback = taskType === 'ack'
      ? `${stem}_Code_of_Conduct.pdf`
      : taskType === 'sign'
        ? `${stem}_NDA.pdf`
        : `${stem}_Document.pdf`;
    setDocName(file?.name || fallback);
    setDocAttached(true);
  }

  function openFilePicker() {
    fileRef.current?.click();
  }

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  function chooseType(next) {
    setTaskType(next);
    if (next === 'form') {
      setDocAttached(false);
      setDocName('');
    } else if (next === 'upload') {
      setDocAttached(false);
      setDocName('');
    } else if (!docAttached) {
      setDocName('');
    }
    setStepIdx(0);
  }

  function save() {
    if (toolId) onCreated?.(toolId);
    onClose();
  }

  return (
    <div className="lp-wz-wrap" role="dialog" aria-modal="true" onClick={handleBackdrop}>
      <div className="lp-wz" onClick={(e) => e.stopPropagation()}>
        <div className="lp-wz-h">
          <h2 className="lp-wz-title">{headerTitle}</h2>
          <button type="button" className="lp-wz-x" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="lp-wz-steps">
          {flow.map((key, i) => (
            <div
              key={key}
              className={`lp-wz-step${i === stepIdx ? ' on' : ''}${i < stepIdx ? ' done' : ''}`}
            >
              <span className="lp-wz-stepn">{i < stepIdx ? '✓' : i + 1}</span>
              <span className="lp-wz-stepl">{STEP_LABELS[key]}</span>
            </div>
          ))}
        </div>

        <div className="lp-wz-body">
          {currentStep === 'action' ? (
            <div className="lp-wz-fields">
              <div className="lp-wz-field">
                <label htmlFor="tsName">Widget name</label>
                <input
                  id="tsName"
                  className="lp-wz-in"
                  type="text"
                  placeholder="Enter a name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="lp-wz-field">
                <label htmlFor="tsAction">Participant action</label>
                <select
                  id="tsAction"
                  className="lp-wz-in"
                  value={taskType}
                  onChange={(e) => chooseType(e.target.value)}
                >
                  {TASK_ACTIONS.map((act) => (
                    <option key={act.id} value={act.id}>{act.name}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          {currentStep === 'doc' ? (
            <>
              <input
                ref={fileRef}
                type="file"
                hidden
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => {
                  attachDoc(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              {docAttached ? (
                <div className="lp-wz-filerow">
                  <div className="lp-wz-fileinfo">
                    <div className="lp-wz-filename">{docName}</div>
                    <div className="lp-wz-filemeta">{actionMeta.docLabel || 'Document'} · ready to send</div>
                  </div>
                  <button type="button" className="lp-wz-ghost" onClick={openFilePicker}>
                    Replace
                  </button>
                </div>
              ) : (
                <div
                  className="lp-wz-drop"
                  role="button"
                  tabIndex={0}
                  onClick={openFilePicker}
                  onKeyDown={(e) => { if (e.key === 'Enter') openFilePicker(); }}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag'); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('drag')}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('drag');
                    attachDoc(e.dataTransfer.files?.[0]);
                  }}
                >
                  <div className="lp-wz-dropmain">Choose a file or drop it here</div>
                  <div className="lp-wz-drophint">PDF or Word document</div>
                  <button
                    type="button"
                    className="lp-wz-primary"
                    onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
                  >
                    Choose file
                  </button>
                </div>
              )}
            </>
          ) : null}

          {currentStep === 'deadline' ? (
            <div className="lp-wz-duegrid">
              <button
                type="button"
                className={`lp-wz-duecard${dueMode === 'date' ? ' on' : ''}`}
                onClick={() => setDueMode('date')}
              >
                <span className="lp-wz-duecard-h">By a date</span>
                <span className="lp-wz-duecard-sub">Everyone gets the same deadline.</span>
                {dueMode === 'date' ? (
                  <input
                    className="lp-wz-in lp-wz-duedate"
                    type="date"
                    value={due}
                    onChange={(e) => setDue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : null}
              </button>
              <span className="lp-wz-or">or</span>
              <button
                type="button"
                className={`lp-wz-duecard${dueMode === 'location' ? ' on' : ''}`}
                onClick={() => setDueMode('location')}
              >
                <span className="lp-wz-duecard-h">Before arrival</span>
                <span className="lp-wz-duecard-sub">
                  The deadline is based on each person&apos;s required arrival date.
                </span>
              </button>
            </div>
          ) : null}

          {currentStep === 'reminders' ? (
            <>
              <div className="lp-wz-lab">Tone</div>
              <div className="lp-wz-tonegrid">
                {TASK_TONES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`lp-wz-duecard${tone === t.id ? ' on' : ''}`}
                    onClick={() => setTone(t.id)}
                  >
                    <span className="lp-wz-duecard-h">{t.name}</span>
                    <span className="lp-wz-duecard-sub">{t.desc}</span>
                  </button>
                ))}
              </div>
              <div className="lp-wz-lab" style={{ marginTop: 22 }}>First message</div>
              <div className="lp-wz-seg">
                <button
                  type="button"
                  className={`lp-wz-segbtn${sendWhen === 'now' ? ' on' : ''}`}
                  onClick={() => setSendWhen('now')}
                >
                  Send now
                </button>
                <button
                  type="button"
                  className={`lp-wz-segbtn${sendWhen === 'hold' ? ' on' : ''}`}
                  onClick={() => setSendWhen('hold')}
                >
                  Wait to send
                </button>
              </div>
              <div className="lp-wz-note">
                {sendWhen === 'now'
                  ? 'Goes out as soon as the task is created.'
                  : 'The task starts paused. You send it from the task list when you are ready.'}
              </div>
            </>
          ) : null}

          {currentStep === 'people' ? (
            <>
              <div className="lp-wz-filters">
                <select
                  className="lp-wz-in"
                  value={needsImm ? 'imm' : ''}
                  onChange={(e) => setNeedsImm(e.target.value === 'imm')}
                  aria-label="Roster filter"
                >
                  <option value="">Anyone on the roster</option>
                  <option value="imm">Only people who need immigration</option>
                </select>
                <select
                  className="lp-wz-in"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  aria-label="Role filter"
                >
                  <option value="">Any role</option>
                  <option value="__above">Above the line</option>
                  <option value="__below">Below the line</option>
                  {roleOptions.above.length ? (
                    <optgroup label="Above the line">
                      {roleOptions.above.map((r) => <option key={r} value={r}>{r}</option>)}
                    </optgroup>
                  ) : null}
                  {roleOptions.below.length ? (
                    <optgroup label="Below the line">
                      {roleOptions.below.map((r) => <option key={r} value={r}>{r}</option>)}
                    </optgroup>
                  ) : null}
                </select>
                <select
                  className="lp-wz-in"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  aria-label="Country filter"
                >
                  <option value="">Any country</option>
                  {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                  className="lp-wz-in"
                  placeholder="Search participants"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="lp-wz-listhead">
                <span>People</span>
                <span className="lp-wz-listcount">{assignLabel}</span>
              </div>
              <div className="lp-wz-list">
                {assigned.length ? (
                  assigned.map((p) => (
                    <div key={p.id} className="lp-wz-listrow">
                      <span className="lp-wz-listnm">{p.name || '-'}</span>
                      <span className="lp-wz-listro">
                        {p.job_title || '-'} · {shootCountryName || '-'}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="lp-wz-note">No one matches.</div>
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className="lp-wz-foot">
          <button
            type="button"
            className="lp-wz-ghost"
            onClick={() => {
              if (stepIdx > 0) setStepIdx((i) => i - 1);
              else if (onBack) onBack();
              else onClose();
            }}
          >
            Back
          </button>
          {isLast ? (
            <button type="button" className="lp-wz-primary" onClick={save}>{saveLabel}</button>
          ) : (
            <button
              type="button"
              className="lp-wz-primary"
              onClick={() => setStepIdx((i) => i + 1)}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TeasePane({ title, body }) {
  return (
    <div className="lp-tease">
      <span className="lp-badge">COMING SOON</span>
      <h2>{title}</h2>
      <p>{body}</p>
    </div>
  );
}

function TasksPane({
  tasksPreview,
  widgetOptions,
  enablingSlug,
  onOpenPicker,
  onOpenRequest,
}) {
  const { t } = useTranslation();
  const tasks = tasksPreview?.tasks || [];

  return (
    <div className="lp-ref-page">
      <div className="lp-ref-page-title">
        <h1>{t('lpWorkbench.tasks')}</h1>
        <button
          type="button"
          className="lp-slate-new-btn"
          onClick={() => onOpenPicker(widgetOptions)}
          disabled={!widgetOptions?.length || Boolean(enablingSlug)}
        >
          {enablingSlug
            ? t('lpWorkbench.savingWidget')
            : widgetOptions?.length
              ? 'Add task'
              : t('lpWorkbench.allWidgetsAdded')}
        </button>
      </div>

      <section className="lp-ref-tasks-list">
        {tasks.length === 0 ? (
          <div className="lp-ref-empty">{t('lpWorkbench.noRequestsYet')}</div>
        ) : (
          tasks.map((task) => {
            const assigned = Number(task.total_count) || 0;
            const complete = Number(task.done_count) || 0;
            const outstanding = Math.max(0, assigned - complete);
            const inProcess = Number(task.in_process_count) || 0;
            const outstandingShown = Math.max(0, outstanding - inProcess);
            const pct = assigned ? Math.round((complete / assigned) * 100) : 0;
            return (
              <article className="lp-ref-task-card" key={task.slug || task.title}>
                <button
                  type="button"
                  className="lp-ref-task-card-hit"
                  aria-label={`Open ${task.title}`}
                  onClick={() => onOpenRequest(task)}
                />
                <header>
                  <span>
                    <strong>{task.title}</strong>
                    <small>{task.is_complete ? t('lpWorkbench.complete') : 'Due before arrival'}</small>
                  </span>
                  <b aria-hidden="true">›</b>
                </header>
                <div
                  className="lp-ref-task-progress"
                  aria-label={`${complete} of ${assigned} complete`}
                >
                  <i style={{ width: `${pct}%` }} />
                </div>
                <dl>
                  <div>
                    <dt>Assigned</dt>
                    <dd>{assigned}</dd>
                  </div>
                  <div>
                    <dt>Complete</dt>
                    <dd>{complete}</dd>
                  </div>
                  <div>
                    <dt>Outstanding</dt>
                    <dd>{outstandingShown}</dd>
                  </div>
                  <div>
                    <dt>In Process</dt>
                    <dd>{inProcess}</dd>
                  </div>
                </dl>
              </article>
            );
          })
        )}
      </section>
    </div>
  );
}

const OVERVIEW_LOCATION_LIMIT = 3;

function OverviewPane({ data, people, shootCountryName, tasksPreview, onGoImmigration, onOpenRequest }) {
  const [showAllLocations, setShowAllLocations] = useState(false);

  const immigration = useMemo(
    () => buildReferenceImmigrationSummary(people, data?.immigration_bucket_summary),
    [people, data?.immigration_bucket_summary],
  );
  const locationSummary = useMemo(
    () => buildReferenceLocationSummary(data, people, shootCountryName),
    [data, people, shootCountryName],
  );

  const itineraryTask = useMemo(() => {
    const tasks = tasksPreview?.tasks || [];
    return tasks.find((t) => /itinerary/i.test(String(t.slug || t.title || ''))) || tasks[0] || null;
  }, [tasksPreview]);

  const visibleLocations = showAllLocations
    ? locationSummary.locations
    : locationSummary.locations.slice(0, OVERVIEW_LOCATION_LIMIT);

  const donutStyle = {
    background: `conic-gradient(var(--slate-green) 0 ${immigration.ringPct * 3.6}deg, #e5e3dd ${immigration.ringPct * 3.6}deg 360deg)`,
  };

  const itinAssigned = Number(itineraryTask?.total_count) || 0;
  const itinComplete = Number(itineraryTask?.done_count) || 0;
  const itinOutstanding = Math.max(0, itinAssigned - itinComplete);
  const itinInProcess = Number(itineraryTask?.in_process_count) || 0;
  const itinOutstandingShown = Math.max(0, itinOutstanding - itinInProcess);
  const itinPct = itinAssigned ? Math.round((itinComplete / itinAssigned) * 100) : 0;

  return (
    <div className="lp-ref-page">
      <div className="lp-ref-overview-grid">
        <section className="lp-ref-immigration-summary">
          <div className="lp-ref-section-title">
            <h2>Immigration status</h2>
            <strong>{immigration.completeLabel}</strong>
          </div>
          <div className="lp-ref-summary-body">
            <div className="lp-ref-donut" style={donutStyle}>
              <span>{immigration.completePct}%</span>
            </div>
            <dl>
              <div>
                <dt>Complete</dt>
                <dd>{immigration.complete}</dd>
              </div>
              <div>
                <dt>In progress</dt>
                <dd>{immigration.inProgress}</dd>
              </div>
              <div>
                <dt>At risk</dt>
                <dd className="lp-ref-risk-number">{immigration.atRisk}</dd>
              </div>
              <div>
                <dt>Not started</dt>
                <dd>{immigration.notStarted}</dd>
              </div>
            </dl>
          </div>
        </section>

        {locationSummary.locations.length ? (
          <section className="lp-ref-location-summary">
            <div className="lp-ref-location-header">
              <h2>{locationSummary.countryName}</h2>
            </div>
            <p>
              {locationSummary.cleared} of {locationSummary.total} cleared ·{' '}
              <b>{locationSummary.atRiskCount} at risk</b>
            </p>
            <ul>
              {visibleLocations.map((loc) => (
                <li key={loc.name}>
                  <button type="button" onClick={onGoImmigration}>
                    <span>{loc.name}</span>
                    <small>{loc.date || '—'}</small>
                    <strong className={loc.atRisk ? 'is-risk' : ''}>{loc.status}</strong>
                  </button>
                </li>
              ))}
            </ul>
            {locationSummary.locations.length > OVERVIEW_LOCATION_LIMIT ? (
              <button
                type="button"
                onClick={() => setShowAllLocations((v) => !v)}
              >
                {showAllLocations
                  ? 'Show fewer locations'
                  : `Show all ${locationSummary.locations.length} locations`}
              </button>
            ) : null}
          </section>
        ) : null}

        {itineraryTask ? (
          <article className="lp-ref-task-card lp-ref-overview-task">
            <button
              type="button"
              className="lp-ref-task-card-hit"
              aria-label={`Open ${itineraryTask.title}`}
              onClick={() => onOpenRequest(itineraryTask)}
            />
            <header>
              <span>
                <strong>{itineraryTask.title}</strong>
                <small>Due before arrival</small>
              </span>
              <b aria-hidden="true">›</b>
            </header>
            <div
              className="lp-ref-task-progress"
              aria-label={`${itinComplete} of ${itinAssigned} complete`}
            >
              <i style={{ width: `${itinPct}%` }} />
            </div>
            <dl>
              <div>
                <dt>Assigned</dt>
                <dd>{itinAssigned}</dd>
              </div>
              <div>
                <dt>Complete</dt>
                <dd>{itinComplete}</dd>
              </div>
              <div>
                <dt>Outstanding</dt>
                <dd>{itinOutstandingShown}</dd>
              </div>
              <div>
                <dt>In Process</dt>
                <dd>{itinInProcess}</dd>
              </div>
            </dl>
          </article>
        ) : null}
      </div>
    </div>
  );
}

function RosterPane({ people, shootCountryName, onOpenPerson }) {
  const [role, setRole] = useState('');
  const [nationality, setNationality] = useState('');
  const [search, setSearch] = useState('');

  const roleOptions = useMemo(() => buildRosterRoleOptions(people), [people]);
  const nationalityOptions = useMemo(() => {
    const nats = new Set();
    (people || []).forEach((p) => {
      if (p.country) nats.add(p.country);
    });
    return [...nats].sort((a, b) => a.localeCompare(b));
  }, [people]);

  const filtered = useMemo(
    () => filterRosterPeople(people, { role, nationality, search }),
    [people, role, nationality, search],
  );

  const sorted = useMemo(() => {
    const rows = [...filtered];
    rows.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    return rows;
  }, [filtered]);

  return (
    <div className="lp-ref-roster-page">
      <div className="lp-ref-roster-tools">
        <label className="lp-ref-roster-select">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            aria-label="Filter by role"
          >
            <option value="">All roles</option>
            {roleOptions.above.length ? (
              <optgroup label="Above the line">
                {roleOptions.above.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </optgroup>
            ) : null}
            {roleOptions.below.length ? (
              <optgroup label="Below the line">
                {roleOptions.below.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </label>
        <label className="lp-ref-roster-select">
          <select
            value={nationality}
            onChange={(e) => setNationality(e.target.value)}
            aria-label="Filter by nationality"
          >
            <option value="">All nationalities</option>
            {nationalityOptions.map((nat) => (
              <option key={nat} value={nat}>{formatParticipantNationality(nat)}</option>
            ))}
          </select>
        </label>
        <label className="lp-ref-search lp-ref-roster-search">
          <span aria-hidden="true">⌕</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            aria-label="Search roster"
          />
        </label>
      </div>

      <div className="lp-ref-roster-table">
        <div className="lp-ref-roster-head">
          <span>Name</span>
          <span>Role</span>
          <span>Nationality</span>
          <span>Country</span>
          <span aria-hidden="true" />
        </div>
        {sorted.length === 0 ? (
          <div className="lp-ref-empty">No people match.</div>
        ) : (
          sorted.map((p) => (
            <button
              type="button"
              className="lp-ref-roster-row"
              key={p.id}
              onClick={() => onOpenPerson(p.id)}
            >
              <strong>{p.name || '-'}</strong>
              <span>{p.job_title || p.department || '-'}</span>
              <span>{formatParticipantNationality(p.country)}</span>
              <span>{shootCountryName || '-'}</span>
              <b aria-hidden="true">›</b>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function invitationSentLabel(emailSentAt) {
  if (!emailSentAt) return 'Today';
  const sent = new Date(emailSentAt);
  if (Number.isNaN(sent.getTime())) return '—';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sentDay = new Date(sent);
  sentDay.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - sentDay.getTime()) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return formatLongDate(emailSentAt) || '—';
}

function ImmigrationPane({
  people,
  shootCountry,
  onOpenPerson,
}) {
  const [riskOnly, setRiskOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [copiedEmails, setCopiedEmails] = useState(false);

  const registeredPeople = useMemo(
    () => (people || []).filter(
      (p) => p.status === 'registered' || Boolean(p.registered_at),
    ),
    [people],
  );

  const notRegisteredPeople = useMemo(
    () => (people || []).filter(
      (p) => !(p.status === 'registered' || Boolean(p.registered_at)),
    ),
    [people],
  );

  const filtered = useMemo(
    () => filterImmigrationPeople(registeredPeople, {
      riskOnly,
      search,
      shootCountry,
      immigrationOnly: false,
    }),
    [registeredPeople, riskOnly, search, shootCountry],
  );

  const attentionCount = useMemo(
    () => registeredPeople.filter(isImmigrationAtRisk).length,
    [registeredPeople],
  );

  const sortedPeople = useMemo(() => {
    const rows = [...filtered];
    rows.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    return rows;
  }, [filtered]);

  async function copyNotRegisteredEmails() {
    const emails = notRegisteredPeople
      .map((p) => String(p.email || '').trim())
      .filter(Boolean);
    if (!emails.length) return;
    try {
      await navigator.clipboard.writeText(emails.join(', '));
      setCopiedEmails(true);
      setTimeout(() => setCopiedEmails(false), 2000);
    } catch {
      /* ignore */
    }
  }

  function renderPersonRow(p, { showEmail = false, openable = true } = {}) {
    const status = immigrationListStatus(p);
    // Needed by = travel / shoot start date for this person.
    const neededBy = formatLongDate(p.schedule_start_date) || '—';
    return (
      <tr
        key={p.id}
        className="lp-dt-row"
        role={openable ? 'button' : undefined}
        tabIndex={openable ? 0 : undefined}
        aria-label={openable ? `Open ${p.name || 'participant'}` : undefined}
        onClick={openable ? () => onOpenPerson(p.id) : undefined}
        onKeyDown={openable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenPerson(p.id);
          }
        } : undefined}
      >
        <td className="lp-dt-who">
          <span>
            {openable ? (
              <button
                type="button"
                className="lp-ref-name-link"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenPerson(p.id);
                }}
              >
                {p.name || '-'}
              </button>
            ) : (
              <strong>{p.name || '-'}</strong>
            )}
            <small>{showEmail ? (p.email || '—') : (p.department || '-')}</small>
          </span>
        </td>
        <td>{formatParticipantNationality(p.country) || '-'}</td>
        <td>{p.job_title || p.department || '-'}</td>
        <td>{neededBy}</td>
        <td>
          <span className={`lp-ref-status is-${status.tone}`}>{status.label}</span>
        </td>
        <td>
          {openable ? (
            <button
              type="button"
              className="lp-ref-table-open"
              aria-label={`Open ${p.name || 'participant'}`}
              onClick={(e) => {
                e.stopPropagation();
                onOpenPerson(p.id);
              }}
            >
              &gt;
            </button>
          ) : null}
        </td>
      </tr>
    );
  }

  return (
    <section className="lp-ref-participants-section">
      <div className="lp-ref-page-title">
        <h1>Immigration</h1>
      </div>

      <div className="lp-ref-section-heading lp-ref-table-heading">
        <h2>Participants</h2>
        <div className="lp-ref-table-tools">
          <label className="lp-ref-search">
            <span aria-hidden="true">⌕</span>
            <input
              placeholder="Search participants"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <button type="button" className="lp-ref-filter-btn">Filter</button>
        </div>
      </div>

      <div className="lp-ref-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={!riskOnly}
          className={!riskOnly ? 'is-on' : ''}
          onClick={() => setRiskOnly(false)}
        >
          All participants
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={riskOnly}
          className={riskOnly ? 'is-on' : ''}
          onClick={() => setRiskOnly(true)}
        >
          Needs attention
          {attentionCount > 0 ? <span>{attentionCount}</span> : null}
        </button>
      </div>

      <div className="lp-ref-table-wrap">
        <table className="lp-ref-table cols-imm">
          <thead>
            <tr>
              <th>Participant</th>
              <th>Nationality</th>
              <th>Role</th>
              <th>Needed by</th>
              <th>Status</th>
              <th aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {sortedPeople.length === 0 ? (
              <tr>
                <td className="lp-dt-empty" colSpan={6}>
                  {registeredPeople.length
                    ? 'No people match these filters.'
                    : 'No registered participants yet.'}
                </td>
              </tr>
            ) : (
              sortedPeople.map((p) => renderPersonRow(p))
            )}
          </tbody>
        </table>
      </div>

      {!riskOnly && notRegisteredPeople.length > 0 ? (
        <section className="lp-ref-invitation-panel" aria-labelledby="lp-not-registered-heading">
          <header>
            <div>
              <h2 id="lp-not-registered-heading">Not registered</h2>
            </div>
            <button type="button" onClick={copyNotRegisteredEmails}>
              {copiedEmails ? 'Copied' : 'Copy email addresses'}
            </button>
          </header>
          <div className="lp-ref-table-wrap">
            <table className="lp-ref-table cols-imm lp-ref-unregistered">
              <thead>
                <tr>
                  <th>Participant</th>
                  <th>Nationality</th>
                  <th>Role</th>
                  <th>Needed by</th>
                  <th>Status</th>
                  <th aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {notRegisteredPeople.map((p) => renderPersonRow(p, { showEmail: true, openable: false }))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </section>
  );
}

export default function OrgProductionDetails() {
  const { t } = useTranslation();
  const { productionId } = useParams();
  const mainTabs = useMemo(
    () => [
      { id: 'overview', label: t('lpWorkbench.tabOverview') },
      { id: 'immigration', label: 'Immigration' },
      { id: 'tasks', label: t('lpWorkbench.tabTasks') },
    ],
    [t],
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const initialTab = searchParams.get('tab');
  const initialSub = searchParams.get('sub');
  const [mainTab, setMainTab] = useState(() => {
    if (initialTab === 'immigration' || initialSub === 'immigration' || initialTab === 'mobility') {
      return 'immigration';
    }
    if (initialTab === 'tasks' || initialTab === 'overview') return initialTab;
    if (initialTab === 'production') return 'overview';
    return 'overview';
  });
  const [showTemplate, setShowTemplate] = useState(false);
  const [showNotReg, setShowNotReg] = useState(false);
  const [taskSetup, setTaskSetup] = useState(null);
  const [widgetPicker, setWidgetPicker] = useState(null);
  const [enablingSlug, setEnablingSlug] = useState('');

  const producerName = getLpName();

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await orgAuthApi.getProduction(productionId);
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setError(t('lpWorkbench.loadProductionError'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [productionId, navigate, t]);

  useEffect(() => { load(); }, [load]);

  const mainTabRef = useRef(mainTab);
  mainTabRef.current = mainTab;

  const selectMainTab = useCallback((nextTab) => {
    const tab = String(nextTab || '').trim();
    if (!tab || tab === mainTabRef.current) return;
    setMainTab(tab);
    load({ silent: true });
  }, [load]);

  const enableProductionRequest = useCallback(async (slug) => {
    const clean = String(slug || '').trim().toLowerCase();
    if (!clean || !productionId) return;
    setEnablingSlug(clean);
    setError('');
    try {
      await orgAuthApi.enableProductionRequest(productionId, clean);
      await load({ silent: true });
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setError('Could not set up this request.');
    } finally {
      setEnablingSlug('');
    }
  }, [productionId, load, navigate]);

  const project = data?.project;
  const enabledRequestSlugs = useMemo(
    () => new Set((project?.enabled_request_slugs || []).map((s) => String(s).toLowerCase())),
    [project?.enabled_request_slugs],
  );
  const availableWidgetOptions = useMemo(
    () => WIDGET_OPTIONS.filter(
      (opt) => !opt.toolId || !enabledRequestSlugs.has(String(opt.toolId).toLowerCase()),
    ),
    [enabledRequestSlugs],
  );
  const people = useMemo(() => data?.participants || [], [data?.participants]);
  const shootCountry = (project?.destination_country || '').toUpperCase();
  const shootCountryName = countryNameFromIso2(shootCountry);
  const taskCountryOptions = useMemo(() => {
    const names = (data?.locations || [])
      .map((row) => countryNameFromIso2(row.country))
      .filter(Boolean);
    if (shootCountryName) names.push(shootCountryName);
    return [...new Set(names)];
  }, [data?.locations, shootCountryName]);

  function openPerson(id, source) {
    const q = source === 'immigration' ? '?from=immigration' : '';
    navigate(`/org/productions/${productionId}/participants/${id}${q}`);
  }

  return (
    <LpWorkbenchShell
      slateTheme
      hideLanguage
      compactProfile
      deleteProductionId={productionId}
      deleteProductionName={project?.name || ''}
    >
      {loading ? (
        <div className="lp-body">
          <div className="lp-empty">{t('lpWorkbench.loadingProduction')}</div>
        </div>
      ) : error ? (
        <div className="lp-body">
          <div className="lp-banner" style={{ background: '#FBF1EF', borderColor: '#E8C4BE' }}>
            <div className="lp-bn-txt"><b>{error}</b></div>
            <button type="button" className="lp-bn-btn" onClick={load}>{t('lpWorkbench.retry')}</button>
          </div>
        </div>
      ) : project ? (
        <>
          <div className="lp-head">
            <div className="lp-prod-nav">
              <div className="lp-prod-title-row">
                <button
                  type="button"
                  className="lp-prod-back"
                  onClick={() => navigate(getOrgSlatePath())}
                  aria-label="Back to productions"
                >
                  ‹
                </button>
                <h1>{project.name}</h1>
              </div>
              <nav className="lp-ptabs" aria-label="Production navigation">
                {mainTabs.map((tabItem) => (
                  <button
                    key={tabItem.id}
                    type="button"
                    className={`lp-ptab${mainTab === tabItem.id ? ' on' : ''}`}
                    onClick={() => selectMainTab(tabItem.id)}
                  >
                    {tabItem.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="lp-body lp-ref-body">
            {mainTab === 'overview' ? (
              <OverviewPane
                data={data}
                people={people}
                shootCountryName={shootCountryName}
                tasksPreview={data?.tasks_preview}
                onGoImmigration={() => selectMainTab('immigration')}
                onOpenRequest={(task) => {
                  if (!task?.slug) return;
                  navigate(`/org/productions/${productionId}/requests/${task.slug}`);
                }}
              />
            ) : null}

            {mainTab === 'immigration' ? (
              <ImmigrationPane
                people={people}
                shootCountry={shootCountry}
                onOpenPerson={(id) => openPerson(id, 'immigration')}
              />
            ) : null}

            {mainTab === 'tasks' ? (
              <TasksPane
                tasksPreview={data?.tasks_preview}
                widgetOptions={availableWidgetOptions}
                enablingSlug={enablingSlug}
                onOpenPicker={setWidgetPicker}
                onOpenRequest={(task) => {
                  if (!task?.slug) return;
                  navigate(`/org/productions/${productionId}/requests/${task.slug}`);
                }}
              />
            ) : null}
          </div>

          {showTemplate ? (
            <InviteTemplateModal
              project={project}
              producerName={producerName}
              onClose={() => setShowTemplate(false)}
            />
          ) : null}
          {showNotReg ? (
            <NotRegisteredModal productionId={productionId} onClose={() => setShowNotReg(false)} />
          ) : null}
          {widgetPicker ? (
            <AddWidgetModal
              options={widgetPicker}
              onClose={() => setWidgetPicker(null)}
              onPick={(opt) => {
                setWidgetPicker(null);
                setTaskSetup({
                  heading: opt.name,
                  title: opt.custom ? '' : opt.name,
                  type: opt.type,
                  fixed: true,
                  toolId: opt.toolId,
                  custom: opt.custom,
                  doc: null,
                  fromPicker: true,
                });
              }}
            />
          ) : null}
          {taskSetup ? (
            <TaskSettingsModal
              setup={taskSetup}
              project={project}
              people={people}
              shootCountryName={shootCountryName}
              countryOptions={taskCountryOptions}
              onClose={() => setTaskSetup(null)}
              onBack={taskSetup.fromPicker ? () => {
                setTaskSetup(null);
                setWidgetPicker(WIDGET_OPTIONS);
              } : null}
              onCreated={(toolId) => {
                if (!toolId) return;
                void enableProductionRequest(toolId);
              }}
            />
          ) : null}
        </>
      ) : null}
    </LpWorkbenchShell>
  );
}
